import { AppDialogService } from './../../services/app-dialog.service';
import { TankService } from 'src/app/services/tank.service';
import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Observable, take } from 'rxjs';
import { IUserInformation } from '../../interfaces/userInformation';
import { OrganizationService } from '../../services/organization.service';
import { IParentOrganizationTree } from '../../interfaces/parentOrganizationTree';
import { ICSOSearchResults } from '../../interfaces/csoSearchResults';
import { AuthService } from '../../services/auth.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../app.state';
import { TranslateService } from '@ngx-translate/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CsoSearchResults } from './models/cso-search-results.model';
import { FilterProfileService } from '../../services/filter-profile.service';
import { getActiveRoutes } from '@utils/utils';

export interface DialogData {
  UserProfileDTO$: Observable<IUserInformation>;
}

@Component({
  selector: 'organization-popup',
  templateUrl: './organization-popup.component.html',
  styleUrls: ['./organization-popup.component.scss'],
})
export class OrganizationPopupComponent implements OnInit {
  resources;
  parentOrganizationTree: IParentOrganizationTree[];
  childOrganizationList: CsoSearchResults[] = [];
  // Which child orgs survive the current filter. Component state, deliberately not a flag on the
  // rows themselves: these rows come from a shared service response, so writing the filter onto
  // them leaked the last search into whatever read that response next - including the next time
  // this dialog opened, which showed an empty org list until the page was refreshed.
  visibleOrgIds: Set<number> = new Set<number>();
  csoSearchResults: ICSOSearchResults[];
  selectedOrganizationId: number = -1;
  showAlphaTab: boolean = true;
  noChildMatches: boolean = false;
  noSearchMatches: boolean = false;
  clickTimer: any;
  isSearchMode: boolean = false;
  searchText: string = '';
  rootOrgId: number;
  highlightMarker: string = '<span>';

  showAll = false;

  hiddenOrgs = [ 'Tanklink', '---Tanklink Direct', '---SMARTank', '---Tanklink Channels', '--Tanklink VAR' ]

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: DialogData,
    private _organizationService: OrganizationService,
    private _authService: AuthService,
    private store: Store<AppState>,
    private translate: TranslateService,
    private _router: Router,
    private dialogRef: MatDialog,
    private appDialogService: AppDialogService,
    private activatedRoute: ActivatedRoute,
    private tankService: TankService,
    private filterProfileService: FilterProfileService,
  ) {}

  ngOnInit(): void {
    // set the currently selected CSO to be the user's last selected organization, and display the parent and child org trees.
    // take(1): UserProfileDTO$ is a live store selector that never completes, so an open-ended
    // subscription outlived the dialog - every popup ever opened re-fetched on each org switch, and
    // the logout store reset (lastSelectedOrganizationId -> 0) made them all request org 0 with no
    // token and pop "error loading organizations" over the login page.
    this.data.UserProfileDTO$.pipe(take(1)).subscribe((u) => {
      this.changeSelectedOrganization(u.lastSelectedOrganizationId);
      this.rootOrgId = u.organizationid;
    });
  }

  // retrieve the currently selected CSO's parent org tree and display it
  getParentOrganizationTree(orgId: number): void {
    this._organizationService
      .getParentOrganizationTree(orgId)
      .subscribe((r) => {
        this.parentOrganizationTree = r;
      });

    this.setSelectedOrganization(orgId);
  }

  // retrieve the currently selected CSO's immediate child org tree and display it
  getChildOrganizationList(orgId: number): void {
    this._organizationService.getChildOrganizationList(orgId).subscribe({
      next: (r) => {
        this.childOrganizationList = r;
        this.showAlphaTab = this.childOrganizationList.length > 20;
        this.resetChildOrgFilters();
      },
      error: () => {
        this.childOrganizationList = [];
        this.resetChildOrgFilters();
        this.appDialogService.showErrorMessage(
          'An error occurred while loading organizations.',
        );
      },
    });
  }

  // =================================
  // click events
  // =================================

  // change to the newly selected CSO and re-display parent and child trees
  selectParentOrganization(orgId: number): void {
    // use timer to avoid both single and dblclick events from firing
    this.clickTimer = setTimeout(() => {
      this.changeSelectedOrganization(orgId);
    }, 300);
  }

  // change to the newly selected CSO and re-display parent and child trees
  changeSelectedOrganization(
    orgId: number,
    forceChange: boolean = false,
  ): void {
    this.isSearchMode = false;
    if (orgId != this.selectedOrganizationId || forceChange === true) {
      this.getParentOrganizationTree(orgId);
      this.getChildOrganizationList(orgId);
    }
  }

  // set the currently selected CSO and handle highlighting
  setSelectedOrganization(orgId: number): void {
    if (this.selectedOrganizationId != orgId) {
      this.selectedOrganizationId = orgId; // set newly selected CSO
    }
  }

  // scroll to the child org by organization name's first letter
  scrollToElementByFirstLetter(firstLetter: string): void {
    // loop thru child elements to find organization to scroll to
    let elems = document.getElementsByClassName('cso-child');

    for (let i = 0; i < elems.length; i++) {
      let data = elems[i].getAttribute('data-orgname');
      if (data >= firstLetter) {
        elems[i].scrollIntoView();
        return;
      }
    }
  }

  // dblclick parent org
  dblclickParentOrganization(orgId: number): void {
    this.setSelectedOrganization(orgId);
    this.updateUserAndClose();
  }

  // =================================
  // update CSO and close dialog
  // =================================

  // set the currently selected CSO and close dialog

  updateUserAndClose(): void {
    clearTimeout(this.clickTimer);
    this.clickTimer = undefined;
    this._authService
      .updateLastSelectedOrganization(this.selectedOrganizationId)
      .subscribe((usr) => {
        this.dialogRef.closeAll();

        // clear filter profile
        this.filterProfileService.clearLastSelectedFilterProfile();

        this.redirectPage();
      });
  }

  // Redirect to the first child root
  redirectPage(): void {
    //get the active route of the first child route node.
    const activeRoutes = getActiveRoutes(this._router.routerState.root);
    if (activeRoutes.length === 0) return; //root and first child should exist.

    //verify we should "redirect" to the first child route node.
    const shouldRedirect =
      activeRoutes.filter((f) => f.snapshot.data?.csoRedirect).length > 0;

    if (!shouldRedirect) return;

    //get the active routes with csoRedirect
    const csoRedirectRoute = activeRoutes.filter((f) => f.snapshot.data?.csoRedirect)[0];
    const path = csoRedirectRoute.snapshot.data?.useParentUrl 
      ? csoRedirectRoute.parent.routeConfig?.path : csoRedirectRoute.routeConfig?.path;

    this._router.navigate([path]);
  }

  // =================================
  // organizationsearch
  // =================================

  // search text has changed
  searchTextChanged(newSearchText: any): void {
    this.searchText = newSearchText;
    if (this.isSearchMode === false) {
      this.filterChildOrganizationList();
    }
  }

  // filter child organization tree
  filterChildOrganizationList(): void {
    this.isSearchMode = false;

    const text: string = this.searchText.toUpperCase();

    this.setVisibleOrgs(
      this.childOrganizationList.filter(
        (child) => child.organizationName.toUpperCase().indexOf(text) >= 0,
      ),
    );
  }

  // reset all child org filters
  resetChildOrgFilters(): void {
    this.setVisibleOrgs(this.childOrganizationList);
  }

  private setVisibleOrgs(orgs: CsoSearchResults[]): void {
    this.visibleOrgIds = new Set<number>(orgs.map((o) => o.organizationID));
    this.noChildMatches = this.visibleOrgIds.size === 0;
  }

  // globally search for organizations
  searchOrganizations(): void {
    if (this.searchText.length < 3) return;
    this.isSearchMode = true;
    this._organizationService
      .getCSOSearchResults(this.searchText, this.rootOrgId)
      .subscribe({
        next: (r) => {
          this.csoSearchResults = r;
          this.noSearchMatches = this.csoSearchResults.length === 0;
        },
        error: (e) => {
          this.appDialogService.showErrorMessage('An error occurred while searching for organizations.');
        },
        complete: () => {},
      });
  }

  // back to organization list
  backToOrgList(): void {
    this.isSearchMode = false;
    this.resetChildOrgFilters();
    this.searchText = '';
  }

  // show unhighlighted section of selected text in org name
  showUnhighlightedText(text: string): string {
    const index: number = text.indexOf(this.highlightMarker);
    const result: string = index <= 0 ? '' : text.substr(0, index);
    return result;
  }

  // show highlighted section of selected text in org name
  showHighlightedText(text: string): string {
    const index: number = text.indexOf(this.highlightMarker);
    const result: string =
      index <= 0 ? text : text.substr(index + this.highlightMarker.length);
    return result;
  }
}
